import { FC, useEffect, ComponentType } from "react";
import { connect } from "react-redux";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { AppDispatch, RootState } from "../store";
import { getAccessToken } from "../store/modules/auth";
import useWindowBreakpoints from "../hooks/useWindowBreakpoints";
import { isTestingAPIEnv, isTestingEnv } from "../utils/app-env";
import { User, UserRole } from "../types/auth.types";
import DetectLocation from "../components/DetectLocation";
import { FilterType } from "../types/filters.types";

interface RestrictedRouteProps {
  requireAccessToken?: boolean;
  role?: UserRole;
  isDetectLocation?: boolean;
  Element: ComponentType | ComponentType<{ loadFilters?: FilterType[] }>;
  isLoggedIn: boolean;
  user: User | null;
  loadFilters?: FilterType[];
  accessToken: string | null;
  getAccessToken: () => void;
}

const RestrictedRoute: FC<RestrictedRouteProps> = (props) => {
  const {
    requireAccessToken = false,
    role,
    isDetectLocation = false,
    Element,
    isLoggedIn,
    user,
    loadFilters = [],
    accessToken,
    getAccessToken,
  } = props;

  const matches = useWindowBreakpoints();

  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  useEffect(() => {
    if (!isTestingEnv) {
      if (
        !isTestingAPIEnv &&
        !isAuthenticated &&
        inProgress === InteractionStatus.None
      ) {
        const account = instance.getActiveAccount();
        if (!account) {
          instance.loginRedirect({
            scopes: ["https://analysis.windows.net/powerbi/api/.default"],
            claims: JSON.stringify({
              access_token: { xms_cc: { values: ["CP1"] } },
            }),
          });
        }
      }
      if (
        (isTestingAPIEnv || isAuthenticated) &&
        inProgress === InteractionStatus.None &&
        !isLoggedIn
      ) {
        getAccessToken();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, inProgress]);

  if (
    !isTestingEnv &&
    ((!isTestingAPIEnv && !isAuthenticated) ||
      !isLoggedIn ||
      (requireAccessToken && !accessToken))
  ) {
    return null;
  }

  if (role && user && user.role !== role) {
    // User does not have the required role to access this route
    return null;
  }

  if (!matches.isInitialized) {
    return null;
  }

  if (isDetectLocation) {
    return (
      <DetectLocation loadFilters={loadFilters}>
        <Element loadFilters={loadFilters} />
      </DetectLocation>
    );
  }

  return <Element loadFilters={loadFilters} />;
};

const mapStateToProps = (state: RootState) => ({
  isLoggedIn: state.auth.isLoggedIn,
  user: state.auth.user,
  accessToken: state.auth.accessToken,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getAccessToken: () => dispatch(getAccessToken()),
});

export default connect(mapStateToProps, mapDispatchToProps)(RestrictedRoute);
